import { useSEO } from "@/lib/useSEO";

const TITLE = "Terms of Use | Parlay Assistant";
const DESCRIPTION =
  "The terms governing use of Parlay Assistant, its free sports betting calculators, and its educational resources.";
const LAST_UPDATED = "February 2026";

const Section = ({ id, title, children }) => (
  <section id={id} className="mt-8">
    <h2 className="font-display text-2xl md:text-3xl uppercase tracking-tight text-slate-900">
      {title}
    </h2>
    <div className="mt-3 text-slate-700 text-sm md:text-base leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

export const TermsPage = () => {
  useSEO({ title: TITLE, description: DESCRIPTION, path: "/terms" });

  return (
    <main
      data-testid="terms-page"
      className="max-w-4xl mx-auto px-5 md:px-8 mt-10 md:mt-14"
    >
      <p className="font-display text-emerald-400 text-xs tracking-[0.25em] uppercase">
        Legal
      </p>
      <h1 className="mt-2 font-display text-white text-4xl sm:text-5xl uppercase tracking-tight leading-[1.02]">
        Terms of Use
      </h1>
      <p className="mt-3 text-slate-400 text-sm">Last Updated: {LAST_UPDATED}</p>

      <div className="mt-8 pa-card p-6 md:p-10">
        <p className="text-slate-700 text-sm md:text-base leading-relaxed">
          Welcome to Parlay Assistant. These Terms of Use (the &ldquo;Terms&rdquo;) govern your
          access to and use of our website and free sports betting tools. Please read them
          carefully.
        </p>

        <Section id="acceptance" title="Acceptance of Terms">
          <p>
            By accessing or using Parlay Assistant, you confirm that you have read, understood,
            and agree to be bound by these Terms. If you do not agree, please discontinue use
            of the site.
          </p>
        </Section>

        <Section id="educational" title="Educational Purposes Only">
          <p>
            Parlay Assistant provides betting calculators, odds converters, and educational
            content strictly for informational and entertainment purposes. Nothing on this site
            is intended as gambling advice, financial advice, or a recommendation to place any
            particular wager.
          </p>
        </Section>

        <Section id="no-gambling" title="No Gambling Services">
          <p>
            Parlay Assistant is not a sportsbook. We do not accept wagers, hold funds, process
            bets, or facilitate transactions between users and sportsbooks. All betting
            activities must take place through licensed operators in accordance with the laws
            of your jurisdiction.
          </p>
        </Section>

        <Section id="calculator-accuracy" title="Calculator Accuracy">
          <p>
            Our calculators use industry-standard formulas to convert odds and estimate parlay
            payouts. Results are calculated in your browser and are provided &ldquo;as
            is,&rdquo; without warranty of any kind. Actual sportsbook payouts may vary from
            our estimates because of sportsbook rules, rounding, voided selections,
            correlated-leg restrictions, promotional adjustments, and other operator-specific
            policies.
          </p>
        </Section>

        <Section id="no-guarantees" title="No Betting Guarantees">
          <p>
            Parlay Assistant does not guarantee betting outcomes, sportsbook payouts, or
            profitability. Sports betting involves risk of loss, and past results are not
            indicative of future performance.
          </p>
        </Section>

        <Section id="user-responsibilities" title="User Responsibilities">
          <p>
            You are responsible for:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Meeting the legal gambling age in your jurisdiction.</li>
            <li>Complying with all local, state, and national laws that apply to your betting activity.</li>
            <li>The accuracy of the inputs you enter into our calculators.</li>
            <li>Using the site in a manner that does not disrupt other users or violate applicable law.</li>
          </ul>
        </Section>

        <Section id="ip" title="Intellectual Property">
          <p>
            All content on Parlay Assistant &mdash; including calculators, code, copy, and
            design &mdash; is the property of Parlay Assistant unless otherwise noted, and is
            protected by applicable intellectual property laws. You may not reproduce, modify,
            or redistribute site content without our prior written permission, except for
            personal, non-commercial use.
          </p>
        </Section>

        <Section id="external-links" title="External Links">
          <p>
            The site may contain links to third-party websites for convenience. We do not
            endorse and are not responsible for the content, products, services, or practices of
            any linked site. Access them at your own risk.
          </p>
        </Section>

        <Section id="sponsor-content" title="Sponsor Content">
          <p>
            Our Sponsors page features paid or promotional partners. Sponsor placement does not
            constitute an endorsement of a specific betting outcome, and clicking a sponsor
            link may earn Parlay Assistant a fee. You should review each sponsor&rsquo;s own
            terms and privacy policies before participating.
          </p>
        </Section>

        <Section id="liability" title="Limitation of Liability">
          <p>
            To the fullest extent permitted by law, Parlay Assistant and its contributors are
            not liable for any direct, indirect, incidental, consequential, or exemplary
            damages arising out of your use of, or inability to use, the site or its
            calculators, including but not limited to losses from wagers placed based on
            calculator estimates.
          </p>
        </Section>

        <Section id="responsible-gambling" title="Responsible Gambling">
          <p>
            Please gamble responsibly. If you or someone you know may have a gambling problem,
            help is available. In the United States you can call 1-800-GAMBLER for
            confidential support 24 hours a day, seven days a week.
          </p>
        </Section>

        <Section id="governing-law" title="Governing Law">
          <p>
            These Terms are governed by the laws of the jurisdiction in which Parlay Assistant
            is operated, without regard to conflict-of-law principles. Any dispute arising out
            of these Terms shall be resolved in the courts of that jurisdiction.
          </p>
        </Section>

        <Section id="changes" title="Changes to Terms">
          <p>
            We may update these Terms from time to time. Updates are effective when posted, and
            the &ldquo;Last Updated&rdquo; date at the top of the page will reflect the change.
            Your continued use of the site after an update constitutes acceptance of the
            revised Terms.
          </p>
        </Section>

        <Section id="contact" title="Contact Information">
          <p>
            Questions about these Terms? Contact us at{" "}
            <a
              href="mailto:advertise@parlayassistant.com"
              className="text-emerald-700 underline hover:text-emerald-800"
            >
              advertise@parlayassistant.com
            </a>
            .
          </p>
        </Section>
      </div>
    </main>
  );
};

export default TermsPage;
